import { ref, computed, inject } from 'vue';
export function useOrders() {
    // Inject API client
    const apiClient = inject('apiClient');
    if (!apiClient) {
        throw new Error('RestaurantApiClient not found - make sure RestaurantPlugin is installed');
    }
    // Cart State
    const cartItems = ref([]);
    const isSubmitting = ref(false);
    const orderError = ref(null);
    // Order State
    const orders = ref([]);
    const currentOrder = ref(null);
    // Cart Computed Properties
    const cartItemCount = computed(() => {
        return cartItems.value.reduce((total, item) => total + item.quantity, 0);
    });
    const cartTotal = computed(() => {
        return cartItems.value.reduce((total, item) => total + item.totalPrice, 0);
    });
    const isEmpty = computed(() => {
        return cartItems.value.length === 0;
    });
    // Cart Methods
    const addToCart = (menuItem, quantity = 1) => {
        const existingItemIndex = cartItems.value.findIndex(item => item.menuItem.id === menuItem.id);
        if (existingItemIndex >= 0) {
            // Update existing item
            const existingItem = cartItems.value[existingItemIndex];
            existingItem.quantity += quantity;
            existingItem.totalPrice = existingItem.quantity * menuItem.price;
        }
        else {
            // Add new item
            const newCartItem = {
                id: `cart_${menuItem.id}_${Date.now()}`,
                menuItem,
                quantity,
                totalPrice: quantity * menuItem.price
            };
            cartItems.value.push(newCartItem);
        }
    };
    const removeFromCart = (itemId) => {
        const index = cartItems.value.findIndex(item => item.id === itemId);
        if (index >= 0) {
            cartItems.value.splice(index, 1);
        }
    };
    const updateCartItemQuantity = (itemId, quantity) => {
        const item = cartItems.value.find(item => item.id === itemId);
        if (item) {
            if (quantity <= 0) {
                removeFromCart(itemId);
            }
            else {
                item.quantity = quantity;
                item.totalPrice = quantity * item.menuItem.price;
            }
        }
    };
    const clearCart = () => {
        cartItems.value = [];
    };
    // Order Methods
    const submitOrder = async (customerInfo, specialInstructions) => {
        if (isEmpty.value) {
            orderError.value = 'Cart is empty';
            return null;
        }
        isSubmitting.value = true;
        orderError.value = null;
        try {
            const orderData = {
                customerInfo,
                items: cartItems.value,
                totalAmount: cartTotal.value,
                specialInstructions
            };
            const response = await apiClient.submitOrder(orderData);
            if (response.status === 'success' && response.data) {
                currentOrder.value = response.data;
                clearCart();
                return response.data;
            }
            else {
                orderError.value = response.message || 'Failed to submit order';
                return null;
            }
        }
        catch (error) {
            console.error('Error submitting order:', error);
            orderError.value = error instanceof Error ? error.message : 'Unknown error occurred';
            return null;
        }
        finally {
            isSubmitting.value = false;
        }
    };
    const getOrderById = async (orderId) => {
        try {
            const response = await apiClient.getOrder(orderId);
            if (response.status === 'success' && response.data) {
                return response.data;
            }
            else {
                orderError.value = response.message || 'Failed to get order';
                return null;
            }
        }
        catch (error) {
            console.error('Error getting order:', error);
            orderError.value = error instanceof Error ? error.message : 'Unknown error occurred';
            return null;
        }
    };
    const getOrderByNumber = async (orderNumber) => {
        try {
            const response = await apiClient.getOrderByNumber(orderNumber);
            if (response.status === 'success' && response.data) {
                return response.data;
            }
            else {
                orderError.value = response.message || 'Failed to get order';
                return null;
            }
        }
        catch (error) {
            console.error('Error getting order by number:', error);
            orderError.value = error instanceof Error ? error.message : 'Unknown error occurred';
            return null;
        }
    };
    const getOrderHistory = async () => {
        try {
            const response = await apiClient.getOrderHistory();
            if (response.status === 'success' && response.data) {
                orders.value = response.data;
                return response.data;
            }
            else {
                orderError.value = response.message || 'Failed to get order history';
                return [];
            }
        }
        catch (error) {
            console.error('Error getting order history:', error);
            orderError.value = error instanceof Error ? error.message : 'Unknown error occurred';
            return [];
        }
    };
    const updateOrderStatus = async (orderId, status) => {
        try {
            const response = await apiClient.updateOrderStatus(orderId, status);
            if (response.status === 'success') {
                // Update local order if it exists
                const orderIndex = orders.value.findIndex(order => order.id === orderId);
                if (orderIndex >= 0) {
                    orders.value[orderIndex].status = status;
                    orders.value[orderIndex].updatedAt = new Date().toISOString();
                }
                if (currentOrder.value?.id === orderId) {
                    currentOrder.value.status = status;
                    currentOrder.value.updatedAt = new Date().toISOString();
                }
                return true;
            }
            else {
                orderError.value = response.message || 'Failed to update order status';
                return false;
            }
        }
        catch (error) {
            console.error('Error updating order status:', error);
            orderError.value = error instanceof Error ? error.message : 'Unknown error occurred';
            return false;
        }
    };
    const cancelOrder = async (orderId, reason) => {
        try {
            const response = await apiClient.cancelOrder(orderId, reason);
            if (response.status === 'success') {
                // Update local order if it exists
                const orderIndex = orders.value.findIndex(order => order.id === orderId);
                if (orderIndex >= 0) {
                    orders.value[orderIndex].status = 'cancelled';
                    orders.value[orderIndex].updatedAt = new Date().toISOString();
                }
                if (currentOrder.value?.id === orderId) {
                    currentOrder.value.status = 'cancelled';
                    currentOrder.value.updatedAt = new Date().toISOString();
                }
                return true;
            }
            else {
                orderError.value = response.message || 'Failed to cancel order';
                return false;
            }
        }
        catch (error) {
            console.error('Error cancelling order:', error);
            orderError.value = error instanceof Error ? error.message : 'Unknown error occurred';
            return false;
        }
    };
    return {
        // Cart State
        cartItems,
        cartItemCount,
        cartTotal,
        isEmpty,
        // Order State
        orders,
        currentOrder,
        isSubmitting,
        orderError,
        // Cart Methods
        addToCart,
        removeFromCart,
        updateCartItemQuantity,
        clearCart,
        // Order Methods
        submitOrder,
        getOrderById,
        getOrderByNumber,
        getOrderHistory,
        updateOrderStatus,
        cancelOrder
    };
}
